<template>
    <el-container class="list-page invoice-list-page">
        <el-header class="document-list-toolbar">
            <div class="document-toolbar-header">
                <div class="list-page-search">
                    <el-input prefix-icon="el-icon-search" v-model="invoiceNumberQuery"
                              :placeholder="$t('invoice.invoiceNumberPlaceholder')"></el-input>
                </div>
                <div class="list-page-header-actions">
                    <el-button class="list-page-add-button" type="primary" :disabled="cantAddInvoice" @click.native.prevent="goAddInvoice">
                        <i class="el-icon-plus"></i>
                        <span>{{$t('invoice.addInvoice')}}</span>
                    </el-button>
                </div>
            </div>
            <div class="invoice-filter-row">
                <div class="invoice-filter-controls">
                    <el-select class="invoice-client-select" v-model="clientUUID" clearable filterable remote
                               :remote-method="searchClientOptions" :loading="clientOptionsLoading"
                               :placeholder="$t('invoice.filterAllClients')" @change="changeInvoiceFilter">
                        <el-option v-for="client in clientOptions" :key="client.innerUUID"
                                   :label="client.name" :value="client.innerUUID"></el-option>
                    </el-select>
                    <el-select class="invoice-status-select" v-model="invoiceStatusFilter"
                               @change="changeInvoiceStatusFilter">
                        <el-option :label="$t('invoice.filterAllStatuses')" value=""></el-option>
                        <el-option :label="$t('invoice.filterUnpaid')" value="unpaid"></el-option>
                        <el-option :label="$t('invoice.filterPaid')" value="paid"></el-option>
                        <el-option :label="$t('invoice.filterOverdue')" value="overdue"></el-option>
                        <el-option :label="$t('invoice.filterUnsent')" value="unsent"></el-option>
                        <el-option :label="$t('invoice.filterSent')" value="sent"></el-option>
                    </el-select>
                    <el-date-picker class="invoice-date-range" v-model="invoiceDateRange" type="daterange"
                                    unlink-panels format="MMM d,yyyy"
                                    :start-placeholder="$t('invoice.filterStartDate')"
                                    :end-placeholder="$t('invoice.filterEndDate')"
                                    @change="changeInvoiceFilter"></el-date-picker>
                    <el-select v-if="companyContextLoaded" class="invoice-year-select" v-model="selectedYear" @change="changeInvoiceYear">
                        <el-option
                                v-for="yearOption in yearOptions"
                                :key="yearOption.key"
                                :label="yearOption.label"
                                :value="yearOption.value"></el-option>
                    </el-select>
                </div>
                <div class="invoice-filter-actions">
                    <el-button class="list-page-export-button" :loading="exporting"
                               :disabled="!companyContextLoaded || loading || listLoadError !== '' || exporting"
                               @click="exportData">
                        <i class="el-icon-download"></i>
                        <span>{{$t('report.export')}}</span>
                    </el-button>
                    <el-button class="list-page-export-button"
                               :disabled="!companyContextLoaded || loading || listLoadError !== ''"
                               @click="toggleSelectionMode">
                        <i :class="selectionMode ? 'el-icon-close' : 'el-icon-check'"></i>
                        <span>{{$t(selectionMode ? 'common.cancel' : 'common.select')}}</span>
                    </el-button>
                </div>
            </div>
        </el-header>
        <el-main class="list-page-main">
            <template>
                <el-alert v-if="clientOptionsLoadError !== ''" :title="clientOptionsLoadError" type="error"
                          :closable="false" show-icon></el-alert>
                <list-batch-actions :selected-count="selectedRows.length" :actions="batchActions"
                                    :loading-action="batchLoadingAction" @action="handleBatchAction"
                                    v-if="selectionMode && listLoadError === ''"></list-batch-actions>
                <el-alert v-if="summaryLoadError !== ''" :title="summaryLoadError" type="error" :closable="false" show-icon></el-alert>
                <div v-else-if="companyContextLoaded" class="list-summary-grid">
                    <button type="button" class="list-summary-card overdue" :class="{'is-selected': activeSummaryFilter === 'overdue'}" :aria-pressed="String(activeSummaryFilter === 'overdue')" @click="applySummaryFilter('overdue')">
                        <div class="list-summary-icon">
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M14 2H6.5A2.5 2.5 0 0 0 4 4.5v15A2.5 2.5 0 0 0 6.5 22h11a2.5 2.5 0 0 0 2.5-2.5V8l-6-6Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
                                <path d="M14 2v6h6" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
                                <path d="M12 11v4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                <path d="M12 18h.01" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                            </svg>
                        </div>
                        <div class="list-summary-content">
                            <div class="list-summary-title">{{$t('invoice.overdueInvoices')}} ({{invoiceSummary.overdueInvoiceCount}})</div>
                            <div class="list-summary-money">{{formatSummaryMoney(invoiceSummary.overdueInvoiceTotalMoney)}}</div>
                        </div>
                    </button>
                    <button type="button" class="list-summary-card paid" :class="{'is-selected': activeSummaryFilter === 'paid'}" :aria-pressed="String(activeSummaryFilter === 'paid')" @click="applySummaryFilter('paid')">
                        <div class="list-summary-icon">
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
                                <path d="m8.5 12.5 2.2 2.2 4.8-5.2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                        <div class="list-summary-content">
                            <div class="list-summary-title">{{$t('invoice.paidInvoices')}} ({{invoiceSummary.paidInvoiceCount}})</div>
                            <div class="list-summary-money">{{formatSummaryMoney(invoiceSummary.paidInvoiceTotalMoney)}}</div>
                        </div>
                    </button>
                    <button type="button" class="list-summary-card unpaid" :class="{'is-selected': activeSummaryFilter === 'unpaid'}" :aria-pressed="String(activeSummaryFilter === 'unpaid')" @click="applySummaryFilter('unpaid')">
                        <div class="list-summary-icon">
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
                                <path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                        <div class="list-summary-content">
                            <div class="list-summary-title">{{$t('invoice.unpaidInvoices')}} ({{invoiceSummary.unpaidInvoiceCount}})</div>
                            <div class="list-summary-money">{{formatSummaryMoney(invoiceSummary.unpaidInvoiceTotalMoney)}}</div>
                        </div>
                    </button>
                    <button type="button" class="list-summary-card unsent" :class="{'is-selected': activeSummaryFilter === 'unsent'}" :aria-pressed="String(activeSummaryFilter === 'unsent')" @click="applySummaryFilter('unsent')">
                        <div class="list-summary-icon">
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="m22 2-7 20-4-9-9-4 20-7Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
                                <path d="M22 2 11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </div>
                        <div class="list-summary-content">
                            <div class="list-summary-title">{{$t('invoice.unsentInvoices')}} ({{invoiceSummary.unsentInvoiceCount}})</div>
                            <div class="list-summary-money">{{formatSummaryMoney(invoiceSummary.unsentInvoiceTotalMoney)}}</div>
                        </div>
                    </button>
                </div>
                <el-alert v-if="listLoadError !== ''" :title="listLoadError" type="error" :closable="false" show-icon></el-alert>
                <div v-else class="list-table-card">
                    <el-table ref="listTable" class="list-table invoice-list-table" @sort-change="sortTable" v-loading="loading" :data="tableData"
                              row-key="innerUUID" @selection-change="handleSelectionChange" @select-all="handleSelectAll"
                              :empty-text="$t('common.noData')"
                              :default-sort="defaultInvoiceSort"
                              @row-click='handleRowHandle'>
                    <el-table-column v-if="selectionMode" type="selection" width="52" :reserve-selection="true"></el-table-column>
                    <el-table-column prop="numPrefix,num" :label="$t('invoice.columnNumber')" sortable="custom"
                                     :sort-orders="['descending', 'ascending']" min-width="130"
                                     class-name="list-no-ellipsis-column">
                        <template slot="header">
                            <span class="list-sort-header" :class="{'is-active': orderName === 'numPrefix,num'}">
                                {{$t('invoice.columnNumber')}}
                                <svg class="list-sort-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path :class="{'is-active': orderName === 'numPrefix,num' && orderTpe === 1}" d="m8 9 4-4 4 4"/>
                                    <path :class="{'is-active': orderName === 'numPrefix,num' && orderTpe === 2}" d="m8 15 4 4 4-4"/>
                                </svg>
                            </span>
                        </template>
                        <template slot-scope="scope"> {{scope.row.numPrefix}}{{scope.row.num}}</template>
                    </el-table-column>
                    <el-table-column prop="userClientName" :label="$t('invoice.columnClient')" min-width="120">
                        <template slot-scope="scope">
                            <span class="list-client-name" v-if="scope.row.userClientName">{{scope.row.userClientName}}</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="invoiceStartDate" :label="userSetting.dateLabel || $t('estimate.columnDate')" :formatter="dateFormat" sortable="custom" :sort-orders="['descending', 'ascending']" min-width="150">
                        <template slot="header">
                            <span class="list-sort-header" :class="{'is-active': orderName === 'invoiceStartDate'}">
                                {{userSetting.dateLabel || $t('estimate.columnDate')}}
                                <svg class="list-sort-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path :class="{'is-active': orderName === 'invoiceStartDate' && orderTpe === 1}" d="m8 9 4-4 4 4"/>
                                    <path :class="{'is-active': orderName === 'invoiceStartDate' && orderTpe === 2}" d="m8 15 4 4 4-4"/>
                                </svg>
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="dueDate" :label="userSetting.dueDateLabel || $t('invoiceDesign.optionDueDate')" sortable="custom" :sort-orders="['descending', 'ascending']" min-width="180"
                                     class-name="list-no-ellipsis-column">
                        <template slot="header">
                            <span class="list-sort-header" :class="{'is-active': orderName === 'dueDate'}">
                                {{userSetting.dueDateLabel || $t('invoiceDesign.optionDueDate')}}
                                <svg class="list-sort-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path :class="{'is-active': orderName === 'dueDate' && orderTpe === 1}" d="m8 9 4-4 4 4"/>
                                    <path :class="{'is-active': orderName === 'dueDate' && orderTpe === 2}" d="m8 15 4 4 4-4"/>
                                </svg>
                            </span>
                        </template>
                        <template slot-scope="scope">
                            <span v-if="formatDueDate2(scope.row.dueDate,scope)">{{formatDueDate2(scope.row.dueDate,scope)}}</span>
                            <span class="list-status-badge list-status-green" v-if="scope.row.paidStatus=== 2 && scope.row.totalMoney>0">{{$t('invoice.statusFullyPaid')}}</span>
                            <span class="list-status-badge list-status-orange" v-else-if="scope.row.paidStatus=== 1 && Date.parse(new Date())<=scope.row.dueDate*1000">{{$t('invoice.statusPartial')}}</span>
                            <span class="list-status-badge list-status-red" v-else-if="Date.parse(new Date())>scope.row.dueDate*1000 && scope.row.totalMoney>0">{{$t('invoice.statusOverdue')}}</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="emailed" :label="$t('invoice.columnStatus')" width="100"
                                     align="center" header-align="center"
                                     class-name="list-status-column"
                                     label-class-name="list-status-column">
                        <template slot-scope="scope">
                            <span class="list-status-badge list-status-blue" v-if="scope.row.emailed=== 0">{{$t('invoice.statusUnsent')}}</span>
                            <span class="list-status-badge list-status-green" v-else-if="scope.row.emailed=== 1">{{$t('invoice.statusSent')}}</span>
                            <span class="list-status-badge list-status-purple" v-else-if="scope.row.emailed=== 2">{{$t('invoice.statusOpened')}}</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="totalMoney" :formatter="amountFormat" :label="userSetting.totalLabel || $t('estimate.columnTotal')" sortable="custom" :sort-orders="['descending', 'ascending']" min-width="130" align="right" header-align="right">
                        <template slot="header">
                            <span class="list-sort-header" :class="{'is-active': orderName === 'totalMoney'}">
                                {{userSetting.totalLabel || $t('estimate.columnTotal')}}
                                <svg class="list-sort-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path :class="{'is-active': orderName === 'totalMoney' && orderTpe === 1}" d="m8 9 4-4 4 4"/>
                                    <path :class="{'is-active': orderName === 'totalMoney' && orderTpe === 2}" d="m8 15 4 4 4-4"/>
                                </svg>
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="balanceDueMoney" :formatter="amountFormat" :label="userSetting.balanceDueLabel || $t('client.columnBalanceDue')" min-width="150" align="right" header-align="right">
                    </el-table-column>
                    <el-table-column width="176" align="right" header-align="right"
                                     class-name="list-action-column"
                                     label-class-name="list-action-column-header">
                        <template slot-scope="scope">
                            <div class="invoice-list-action-group" @click.stop>
                                <button type="button" class="invoice-list-quick-action" :aria-label="$t('common.print')"
                                        :title="$t('common.print')" @click.stop="printDocument(scope.row)">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <path d="M6 9V2h12v7"/>
                                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                                        <rect x="6" y="14" width="12" height="8"/>
                                    </svg>
                                </button>
                                <button type="button" class="invoice-list-quick-action" :aria-label="$t('common.send')"
                                        :title="$t('common.send')" @click.stop="sendDocument(scope.row)">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <path d="m22 2-7 20-4-9-9-4 20-7Z"/>
                                        <path d="M22 2 11 13"/>
                                    </svg>
                                </button>
                                <button type="button" class="invoice-list-quick-action"
                                        :aria-label="$t('invoice.actionRecordPayment')" :title="$t('invoice.actionRecordPayment')"
                                        @click.stop="openRecordPaymentDialog(scope.row)">
                                    <i class="el-icon-money" aria-hidden="true"></i>
                                </button>
                                <el-dropdown trigger="click" @command="handleInvoiceAction($event, scope.row)" @click.native.stop>
                                    <button type="button" class="invoice-list-action-button" @click.stop :aria-label="$t('common.moreActions')">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2F77F1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                            <circle cx="12" cy="12" r="1"/>
                                            <circle cx="12" cy="5" r="1"/>
                                            <circle cx="12" cy="19" r="1"/>
                                        </svg>
                                    </button>
                                    <el-dropdown-menu slot="dropdown" class="invoice-list-action-menu">
                                        <el-dropdown-item command="markAsSent">{{$t('invoice.actionMarkAsSent')}}</el-dropdown-item>
                                        <el-dropdown-item command="copy" divided>{{$t('invoice.actionCopy')}}</el-dropdown-item>
                                        <el-dropdown-item command="delete">{{$t('invoice.actionDelete')}}</el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </div>
                        </template>
                    </el-table-column>
                </el-table>
                </div>
                <div v-show="stickyScrollbarVisible"
                     ref="stickyScrollbar"
                     class="invoice-sticky-scrollbar"
                     tabindex="0"
                     role="scrollbar"
                     aria-orientation="horizontal"
                     aria-label="Invoice table horizontal scrollbar"
                     :aria-valuemin="0"
                     :aria-valuemax="Math.max(0, stickyScrollbarContentWidth - stickyScrollbarViewportWidth)"
                     :aria-valuenow="stickyScrollbarScrollLeft"
                     :style="{left: stickyScrollbarLeft + 'px', width: stickyScrollbarViewportWidth + 'px'}"
                     @keydown.left.prevent="scrollInvoiceTable(-1)"
                     @keydown.right.prevent="scrollInvoiceTable(1)">
                    <div ref="stickyScrollbarTrack"
                         class="invoice-sticky-scrollbar-track"
                         @mousedown="moveInvoiceTableFromStickyTrack">
                        <div class="invoice-sticky-scrollbar-thumb"
                             :style="{
                                 width: stickyScrollbarThumbWidth + 'px',
                                 transform: 'translateX(' + stickyScrollbarThumbLeft + 'px)'
                             }"
                             @mousedown.stop.prevent="startInvoiceStickyScrollbarDrag"></div>
                    </div>
                </div>
                <div v-if="listLoadError === ''" class="list-pagination">
                    <el-pagination
                            :key="'invoice-pagination-' + currentPage + '-' + pageSize"
                            @size-change="changeTotalCount"
                            @current-change="changePage"
                            :current-page="currentPage"
                            :page-sizes="[50, 100, 200, 400]"
                            :page-size="pageSize"
                            layout="total, sizes, prev, pager, next, jumper"
                            :total="totalCount">
                    </el-pagination>
                </div>

            </template>
        </el-main>
        <el-dialog :title="$t('invoice.recordPaymentDialogTitle')" :visible.sync="recordPaymentDialog" width="520px" custom-class="record-payment-dialog">
            <el-form class="record-payment-form" :model="recordPaymentForm" label-position="top">
                <div class="record-payment-switch-row">
                    <span>{{$t('invoice.markAsFullyPaid')}}</span>
                    <el-switch v-model="recordPaymentForm.isFullPay" :active-value=1 :inactive-value=0
                               @change="handleRecordPaymentFullPayChange"></el-switch>
                </div>
                <el-form-item :label="$t('invoice.formAmount')">
                    <el-input v-model="recordPaymentForm.paidMoney" inputmode="decimal"
                              @input="handleRecordPaymentAmountInput"></el-input>
                </el-form-item>
                <el-form-item :label="$t('invoice.formDate')">
                    <el-date-picker class="record-payment-date-picker" type="date"
                                    v-model="recordPaymentForm.paidDate" :clearable=false></el-date-picker>
                </el-form-item>
                <el-form-item :label="$t('invoice.formPaymentMethod')">
                    <el-input v-model="recordPaymentForm.paidType"></el-input>
                </el-form-item>
                <el-form-item :label="$t('invoice.formNote')">
                    <el-input type="textarea" :rows="3" v-model="recordPaymentForm.note"></el-input>
                </el-form-item>
            </el-form>
            <div slot="footer" class="dialog-footer record-payment-footer">
                <el-button @click="recordPaymentDialog = false">{{$t('common.cancel')}}</el-button>
                <el-button type="primary" :loading="recordPaymentLoading"
                           @click="saveRecordPayment">{{$t('common.save')}}</el-button>
            </div>
        </el-dialog>
        <el-dialog :title="$t('invoice.copyDialogTitle')" :visible.sync="copyDialog" width="760px"
                   custom-class="document-copy-dialog" :close-on-click-modal="false">
            <div class="document-copy-form">
                <div class="document-copy-section">
                    <div class="document-copy-section-title">{{$t('invoice.copyTargetType')}}</div>
                    <el-radio-group class="document-copy-targets" v-model="copyForm.targetType">
                        <el-radio class="document-copy-target" :label="0">
                            <span class="document-copy-target-content"><i class="el-icon-document"></i>{{$t('default.invoice')}}</span>
                        </el-radio>
                        <el-radio class="document-copy-target" :label="1">
                            <span class="document-copy-target-content"><i class="el-icon-date"></i>{{$t('default.estimate')}}</span>
                        </el-radio>
                        <el-radio class="document-copy-target" :label="2">
                            <span class="document-copy-target-content"><i class="el-icon-tickets"></i>{{$t('default.purchaseOrder')}}</span>
                        </el-radio>
                    </el-radio-group>
                </div>
                <div class="document-copy-section">
                    <div class="document-copy-section-title">{{$t('invoice.copyOptions')}}</div>
                    <div class="document-copy-options">
                        <el-checkbox class="document-copy-option" v-model="copyForm.copyClient">
                            <span><i class="el-icon-user"></i>{{$t('invoice.copyClient')}}</span>
                        </el-checkbox>
                        <el-checkbox class="document-copy-option" v-model="copyForm.copyItems">
                            <span><i class="el-icon-goods"></i>{{$t('invoice.copyItems')}}</span>
                        </el-checkbox>
                        <el-checkbox class="document-copy-option" v-model="copyForm.copyAttachments">
                            <span><i class="el-icon-paperclip"></i>{{$t('invoice.copyAttachments')}}</span>
                        </el-checkbox>
                    </div>
                </div>
            </div>
            <div slot="footer" class="dialog-footer document-copy-footer">
                <el-button @click="copyDialog = false">{{$t('common.cancel')}}</el-button>
                <el-button type="primary" :loading="copyLoading" @click="performCopy">{{$t('invoice.actionCopy')}}</el-button>
            </div>
        </el-dialog>
        <document-quick-actions ref="documentQuickActions" :create-type="0" document-name="Invoice"
                                :company-id="companyUUID" :company-name="companyName" :setting="userSetting"
                                @sent="handleQuickSendSuccess"></document-quick-actions>
    </el-container>
</template>

<script>
    import {formatEnglishDate, getDaysByDateString} from '@/utils/time'
    import {toTmpStartOfDayTime} from '@/utils/validate'
    import {debounce} from '@/utils/debounce'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {syncCompanySelection} from '@/utils/company'
    import {getBrowserStartOfTodayEpochSecond, getBrowserTimeZone} from '@/utils/browserTimeZone'
    import {
        getStoredCurrentPlanName,
        getStoredDocumentCreationAccess,
        openFastSpringPage,
        SUBSCRIPTION_ACCESS_ALLOWED,
        SUBSCRIPTION_ACCESS_PREMIUM_REQUIRED
    } from '@/utils/subscription'
    import {readListPagination, writeListPagination} from '@/utils/listPagination'
    import {readListQueryState, writeListQueryState} from '@/utils/listQueryState'
    import {downloadDataExport} from '@/utils/dataExport'
    import {limitServerNumber} from '@/utils/serverNumberLimit'
    import {validateDocumentDetailResponse} from '@/utils/documentDetailValidation'
    import {openListDetail} from '@/utils/listNavigation'
    import DocumentQuickActions from '@/components/common/DocumentQuickActions'
    import ListBatchActions from '@/components/common/ListBatchActions'

    export default {
        components: {
            DocumentQuickActions,
            ListBatchActions
        },
        computed: {
            defaultInvoiceSort() {
                if (this.orderTpe === 1) {
                    return {prop: this.orderName, order: "ascending"};
                }
                if (this.orderTpe === 2) {
                    return {prop: this.orderName, order: "descending"};
                }
                return {};
            },
            yearOptions() {
                let yearList = [{
                    key: "all",
                    label: this.$t('invoice.allYears'),
                    value: null
                }];
                let currentYear = new Date().getFullYear();
                for (let i = currentYear + 1; i >= 2018; i--) {
                    yearList.push({
                        key: String(i),
                        label: String(i),
                        value: i
                    });
                }
                return yearList;
            },
            timePeriodType() {
                if (this.selectedYear === null) {
                    return "ALL_TIME";
                }
                return "CALENDAR_YEAR";
            },
            batchActions() {
                return [
                    {key: 'print', label: this.$t('common.print'), icon: 'el-icon-printer'},
                    {key: 'sendEmail', label: this.$t('common.sendEmail'), icon: 'el-icon-message'},
                    {key: 'markPaid', label: this.$t('invoice.markAsFullyPaid'), icon: 'el-icon-circle-check'},
                    {key: 'markSent', label: this.$t('invoice.actionMarkAsSent'), icon: 'el-icon-position'},
                    {key: 'delete', label: this.$t('common.delete'), icon: 'el-icon-delete', danger: true}
                ];
            }
        },
        methods: {
            exportData() {
                this.exporting = true;
                return downloadDataExport(this.$axios, 'INVOICE', this.buildInvoiceListParams()).catch(() => {
                    this.$message.error(this.$t('common.systemError'));
                }).finally(() => {
                    this.exporting = false;
                });
            },
            printDocument(row) {
                this.$refs.documentQuickActions.printDocument(row);
            },
            sendDocument(row) {
                this.$refs.documentQuickActions.openSendDialog(row);
            },
            handleSelectionChange(selection) {
                this.selectedRows = selection;
            },
            handleSelectAll(selection) {
                const selectedUUIDSet = new Set(selection.map(row => row.innerUUID));
                const allCurrentPageSelected = this.tableData.length > 0
                    && this.tableData.every(row => selectedUUIDSet.has(row.innerUUID));
                if (!allCurrentPageSelected) {
                    this.clearSelection();
                }
            },
            clearSelection() {
                this.selectedRows = [];
                if (this.$refs.listTable) {
                    this.$refs.listTable.clearSelection();
                }
            },
            scrollInvoiceTable(direction) {
                if (this.$refs.listTable == null) {
                    return;
                }
                const tableBody = this.$refs.listTable.$el.querySelector('.el-table__body-wrapper');
                if (tableBody == null) {
                    return;
                }
                tableBody.scrollBy({
                    left: direction * Math.max(240, tableBody.clientWidth * 0.7),
                    behavior: 'smooth'
                });
            },
            refreshInvoiceStickyScrollbar() {
                this.$nextTick(() => {
                    if (this.$refs.listTable == null || this.$refs.stickyScrollbar == null) {
                        return;
                    }
                    const tableBody = this.$refs.listTable.$el.querySelector('.el-table__body-wrapper');
                    if (tableBody == null) {
                        return;
                    }
                    if (this.invoiceTableScrollBody !== tableBody) {
                        if (this.invoiceTableScrollBody != null) {
                            this.invoiceTableScrollBody.removeEventListener(
                                'scroll', this.syncStickyScrollbarFromInvoiceTable
                            );
                        }
                        this.invoiceTableScrollBody = tableBody;
                        this.invoiceTableScrollBody.addEventListener(
                            'scroll', this.syncStickyScrollbarFromInvoiceTable, {passive: true}
                        );
                    }
                    const tableBounds = tableBody.getBoundingClientRect();
                    this.stickyScrollbarLeft = Math.max(0, tableBounds.left);
                    this.stickyScrollbarViewportWidth = Math.max(
                        0, Math.min(tableBounds.width, window.innerWidth - this.stickyScrollbarLeft)
                    );
                    this.stickyScrollbarContentWidth = tableBody.scrollWidth;
                    this.stickyScrollbarVisible = window.innerWidth >= 681
                        && tableBody.scrollWidth > tableBody.clientWidth + 1;
                    const trackWidth = Math.max(0, this.stickyScrollbarViewportWidth - 8);
                    this.stickyScrollbarThumbWidth = Math.min(
                        trackWidth,
                        Math.max(56, trackWidth * tableBody.clientWidth / tableBody.scrollWidth)
                    );
                    this.syncStickyScrollbarFromInvoiceTable();
                });
            },
            syncStickyScrollbarFromInvoiceTable() {
                if (this.invoiceTableScrollBody == null) {
                    return;
                }
                this.stickyScrollbarScrollLeft = Math.round(this.invoiceTableScrollBody.scrollLeft);
                const tableMaxScroll = this.invoiceTableScrollBody.scrollWidth
                    - this.invoiceTableScrollBody.clientWidth;
                const thumbMaxTravel = this.stickyScrollbarViewportWidth - 8
                    - this.stickyScrollbarThumbWidth;
                this.stickyScrollbarThumbLeft = tableMaxScroll > 0
                    ? thumbMaxTravel * this.invoiceTableScrollBody.scrollLeft / tableMaxScroll
                    : 0;
            },
            setInvoiceScrollFromStickyThumb(thumbLeft) {
                if (this.invoiceTableScrollBody == null) {
                    return;
                }
                const thumbMaxTravel = this.stickyScrollbarViewportWidth - 8
                    - this.stickyScrollbarThumbWidth;
                const normalizedThumbLeft = Math.max(0, Math.min(thumbMaxTravel, thumbLeft));
                const tableMaxScroll = this.invoiceTableScrollBody.scrollWidth
                    - this.invoiceTableScrollBody.clientWidth;
                this.invoiceTableScrollBody.scrollLeft = thumbMaxTravel > 0
                    ? tableMaxScroll * normalizedThumbLeft / thumbMaxTravel
                    : 0;
                this.syncStickyScrollbarFromInvoiceTable();
            },
            moveInvoiceTableFromStickyTrack(event) {
                const trackBounds = this.$refs.stickyScrollbarTrack.getBoundingClientRect();
                this.setInvoiceScrollFromStickyThumb(
                    event.clientX - trackBounds.left - this.stickyScrollbarThumbWidth / 2
                );
            },
            startInvoiceStickyScrollbarDrag(event) {
                this.stickyScrollbarDragStartX = event.clientX;
                this.stickyScrollbarDragStartLeft = this.stickyScrollbarThumbLeft;
                document.addEventListener('mousemove', this.dragInvoiceStickyScrollbar);
                document.addEventListener('mouseup', this.stopInvoiceStickyScrollbarDrag);
            },
            dragInvoiceStickyScrollbar(event) {
                this.setInvoiceScrollFromStickyThumb(
                    this.stickyScrollbarDragStartLeft
                    + event.clientX - this.stickyScrollbarDragStartX
                );
            },
            stopInvoiceStickyScrollbarDrag() {
                document.removeEventListener('mousemove', this.dragInvoiceStickyScrollbar);
                document.removeEventListener('mouseup', this.stopInvoiceStickyScrollbarDrag);
            },
            toggleSelectionMode() {
                this.selectionMode = !this.selectionMode;
                if (!this.selectionMode) {
                    this.clearSelection();
                }
                this.refreshInvoiceStickyScrollbar();
            },
            handleBatchAction(action) {
                if (action === 'print') {
                    this.$refs.documentQuickActions.printDocuments(this.selectedRows);
                    return;
                }
                if (action === 'sendEmail') {
                    this.$refs.documentQuickActions.openBatchSendDialog(this.selectedRows);
                    return;
                }
                if (action === 'delete') {
                    this.$confirm(this.$t('common.batchDeleteConfirm', {count: this.selectedRows.length}), this.$t('common.warning'), {
                        confirmButtonText: this.$t('common.ok'),
                        cancelButtonText: this.$t('common.cancel'),
                        type: 'warning'
                    }).then(() => this.executeBatchMutation('DELETE', action)).catch(() => {});
                    return;
                }
                if (action === 'markPaid') {
                    this.executeBatchMutation('MARK_PAID', action);
                    return;
                }
                this.executeBatchMutation('MARK_SENT', action);
            },
            executeBatchMutation(action, loadingAction) {
                this.batchLoadingAction = loadingAction;
                return this.$axios.post('web_batch_action', {
                    params: {
                        entityType: 'DOCUMENT',
                        action: action,
                        documentType: 0,
                        companyUUID: this.companyUUID,
                        innerUUIDList: this.selectedRows.map(row => row.innerUUID)
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        if (response.data.skippedCount > 0) {
                            this.$message.success(this.$t('common.batchActionPartial', {
                                processed: response.data.processedCount,
                                skipped: response.data.skippedCount
                            }));
                        } else {
                            this.$message.success(this.$t('common.batchActionSuccess', {count: response.data.processedCount}));
                        }
                        if (action === 'DELETE') {
                            this.moveToExistingInvoicePage(response.data.processedCount);
                        }
                        this.clearSelection();
                        this.queryInvoiceDashboardSummary();
                        this.queryList();
                    }
                }).finally(() => {
                    this.batchLoadingAction = '';
                });
            },
            moveToExistingInvoicePage(deletedCount) {
                const remainingCount = Math.max(0, this.totalCount - deletedCount);
                const lastPage = Math.max(1, Math.ceil(remainingCount / this.pageSize));
                if (this.currentPage > lastPage) {
                    this.currentPage = lastPage;
                    this.saveListPagination();
                }
            },
            handleQuickSendSuccess() {
                this.clearSelection();
                this.queryInvoiceDashboardSummary();
                this.queryList();
            },
            restoreListPagination() {
                if (Object.keys(this.$route.query).length > 0) {
                    return;
                }
                const pagination = readListPagination('invoice');
                if (pagination != null) {
                    this.currentPage = pagination.currentPage;
                    this.pageSize = pagination.pageSize;
                }
            },
            restoreListQueryState() {
                if (Object.keys(this.$route.query).length > 0) {
                    return;
                }
                const queryState = readListQueryState('invoice');
                if (queryState == null) {
                    return;
                }
                this.currentPage = queryState.currentPage;
                this.pageSize = queryState.pageSize;
                this.invoiceNumberQuery = queryState.invoiceNumberQuery;
                this.invoiceStatusFilter = queryState.invoiceStatusFilter;
                this.invoiceDateRange = queryState.invoiceDateRange == null
                    ? null : queryState.invoiceDateRange.map(timestamp => new Date(timestamp));
                this.selectedYear = queryState.selectedYear;
                this.activeSummaryFilter = queryState.activeSummaryFilter;
                this.paidStatus = queryState.paidStatus;
                this.emailed = queryState.emailed;
                this.overdue = queryState.overdue;
                this.invoiceInnerUUID = queryState.invoiceInnerUUID;
                this.clientUUID = queryState.clientUUID;
                this.orderName = queryState.orderName;
                this.orderTpe = queryState.orderTpe;
            },
            saveListQueryState() {
                writeListQueryState('invoice', {
                    currentPage: this.currentPage,
                    pageSize: this.pageSize,
                    invoiceNumberQuery: this.invoiceNumberQuery,
                    invoiceStatusFilter: this.invoiceStatusFilter,
                    invoiceDateRange: this.invoiceDateRange == null
                        ? null : this.invoiceDateRange.map(date => new Date(date).getTime()),
                    selectedYear: this.selectedYear,
                    activeSummaryFilter: this.activeSummaryFilter,
                    paidStatus: this.paidStatus,
                    emailed: this.emailed,
                    overdue: this.overdue,
                    invoiceInnerUUID: this.invoiceInnerUUID,
                    clientUUID: this.clientUUID,
                    orderName: this.orderName,
                    orderTpe: this.orderTpe
                });
            },
            saveListPagination() {
                writeListPagination('invoice', this.currentPage, this.pageSize);
            },
            goAddInvoice() {
                const access = getStoredDocumentCreationAccess();
                if (access !== SUBSCRIPTION_ACCESS_ALLOWED) {
                    openFastSpringPage(
                        getStoredCurrentPlanName(),
                        access === SUBSCRIPTION_ACCESS_PREMIUM_REQUIRED ? 'Premium' : 'Pro'
                    );
                    return;
                }

                let self = this;
                self.$router.push('/invoiceCreate');
            },
            queryCompanyAndInvoiceList() {
                let that = this;
                this.loading = true;
                this.cantAddInvoice = true;
                this.$axios.post('query_setting_and_company', {
                    params: {}
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        const selectedData = syncCompanySelection(response.data);
                        if (selectedData == null) {
                            throw new Error('Selected company data is missing');
                        }
                        that.companyUUID = selectedData.company.innerUUID;
                        that.companyName = selectedData.company.companyName;
                        that.restoreListPagination();
                        that.restoreListQueryState();
                        that.$nextTick(() => {
                            that.searchWatcherPaused = false;
                        });
                        that.userSetting = selectedData.setting;
                        that.companyContextLoaded = true;
                        that.loadInitialClientOptions();
                        that.queryInvoiceDashboardSummary();
                        that.queryList();
                        return;
                    }
                    that.loading = false;
                    that.listLoadError = that.$t('invoice.listLoadFailed');
                    that.summaryLoadError = that.$t('invoice.summaryLoadFailed');
                }).catch(function () {
                    that.loading = false;
                    that.listLoadError = that.$t('invoice.listLoadFailed');
                    that.summaryLoadError = that.$t('invoice.summaryLoadFailed');
                })
            },
            queryList() {
                const requestSequence = ++this.listRequestSequence;
                let that = this;
                this.loading = true;
                this.listLoadError = "";
                this.cantAddInvoice = true;
                let params = this.buildInvoiceListParams();
                this.$axios.post('query_invoice_list_user_web', {
                    params: params
                }).then(response => {
                    if (requestSequence !== that.listRequestSequence) {
                        return;
                    }
                    if (response.data.errorCode !== 0) {
                        throw new Error('Invoice list query failed');
                    }
                    const dataList = Array.isArray(response.data.dataList) ? response.data.dataList : [];
                    that.tableData = dataList.filter(invoice => invoice != null
                        && (invoice.companyUUID == null || invoice.companyUUID === that.companyUUID)
                        && (invoice.type == null || Number(invoice.type) === 0)
                        && Number(invoice.syncStatus) !== 1);
                    const totalCount = Number(response.data.totalCount);
                    that.totalCount = Number.isFinite(totalCount) && totalCount >= 0
                        ? Math.floor(totalCount) : that.tableData.length;
                    that.totalTypeCount = response.data.totalTypeCount;
                    that.loading = false;
                    that.cantAddInvoice = false;
                    that.refreshInvoiceStickyScrollbar();

                }).catch(function () {
                    if (requestSequence === that.listRequestSequence) {
                        that.loading = false;
                        that.cantAddInvoice = false;
                        that.listLoadError = that.$t('invoice.listLoadFailed');
                    }
                })
            },
            queryInvoiceDashboardSummary() {
                const requestSequence = ++this.summaryRequestSequence;
                this.summaryLoadError = "";
                let params = {
                    type: 0,
                    companyUUID: this.companyUUID,
                    nowTime: getBrowserStartOfTodayEpochSecond(),
                    timeZone: getBrowserTimeZone(),
                    timePeriodType: this.timePeriodType
                };
                if (this.selectedYear !== null) {
                    params.year = this.selectedYear;
                }
                this.$axios.post('query_invoice_dashboard_summary', {
                    params: params
                }).then(response => {
                    if (requestSequence !== this.summaryRequestSequence) {
                        return;
                    }
                    if (response.data.errorCode !== 0 || response.data.objectData == null) {
                        throw new Error('Invoice summary query failed');
                    }
                    this.invoiceSummary = response.data.objectData;
                }).catch(() => {
                    if (requestSequence === this.summaryRequestSequence) {
                        this.summaryLoadError = this.$t('invoice.summaryLoadFailed');
                    }
                })
            },
            buildInvoiceListParams() {
                let params = {
                    currentPage: this.currentPage,
                    pageSize: this.pageSize,
                    invoiceNumberQuery: this.invoiceNumberQuery,
                    orderName: this.orderName,
                    orderTpe: this.orderTpe,
                    type: 0,
                    companyUUID: this.companyUUID,
                    timePeriodType: this.timePeriodType,
                    nowTime: getBrowserStartOfTodayEpochSecond(),
                    timeZone: getBrowserTimeZone()
                };
                if (this.selectedYear !== null) {
                    params.year = this.selectedYear;
                }
                if (this.paidStatus !== null) {
                    params.paidStatus = this.paidStatus;
                }
                if (this.emailed !== null) {
                    params.emailed = this.emailed;
                }
                if (this.overdue !== null) {
                    params.overdue = this.overdue;
                }
                if (this.invoiceInnerUUID !== "") {
                    params.innerUUID = this.invoiceInnerUUID;
                }
                if (this.clientUUID !== "") {
                    params.clientUUID = this.clientUUID;
                }
                if (this.invoiceDateRange != null && this.invoiceDateRange.length === 2) {
                    params.startInvoiceDate = parseInt(toTmpStartOfDayTime(this.invoiceDateRange[0]), 10);
                    const endDate = new Date(this.invoiceDateRange[1]);
                    endDate.setDate(endDate.getDate() + 1);
                    params.endInvoiceDate = parseInt(toTmpStartOfDayTime(endDate), 10);
                }
                return params;
            },
            applySummaryFilter(filterType) {
                this.clearSelection();
                this.currentPage = 1;
                this.saveListPagination();
                this.paidStatus = null;
                this.emailed = null;
                this.overdue = null;
                if (this.activeSummaryFilter === filterType) {
                    this.activeSummaryFilter = "";
                    this.invoiceStatusFilter = "";
                    this.queryList();
                    return;
                }
                this.activeSummaryFilter = filterType;
                this.invoiceStatusFilter = filterType;
                if (filterType === "overdue") {
                    this.overdue = 1;
                    this.paidStatus = 0;
                } else if (filterType === "paid") {
                    this.paidStatus = 2;
                } else if (filterType === "unpaid") {
                    this.paidStatus = 0;
                } else if (filterType === "unsent") {
                    this.emailed = 0;
                }
                this.queryList();
            },
            changeInvoiceYear() {
                this.clearSelection();
                this.currentPage = 1;
                this.saveListPagination();
                this.queryInvoiceDashboardSummary();
                this.queryList();
            },
            changeInvoiceFilter() {
                this.clearSelection();
                this.currentPage = 1;
                this.saveListPagination();
                this.queryList();
            },
            changeInvoiceStatusFilter(value) {
                this.paidStatus = null;
                this.emailed = null;
                this.overdue = null;
                this.activeSummaryFilter = value;
                if (value === 'paid') {
                    this.paidStatus = 2;
                } else if (value === 'unpaid') {
                    this.paidStatus = 0;
                } else if (value === 'overdue') {
                    this.overdue = 1;
                    this.paidStatus = 0;
                } else if (value === 'unsent') {
                    this.emailed = 0;
                } else if (value === 'sent') {
                    this.emailed = 1;
                }
                this.changeInvoiceFilter();
            },
            searchClientOptions(keyword) {
                const requestSequence = ++this.clientOptionsRequestSequence;
                this.clientOptionsLoading = true;
                this.clientOptionsLoadError = '';
                this.$axios.post('query_user_client_user_web', {
                    params: {
                        currentPage: 1,
                        pageSize: 50,
                        userWebCommonQuery: keyword,
                        orderName: 'name',
                        orderTpe: 1,
                        delStatus: 0,
                        companyUUID: this.companyUUID
                    }
                }).then(response => {
                    if (requestSequence !== this.clientOptionsRequestSequence) {
                        return;
                    }
                    if (response.data.errorCode !== 0) {
                        throw new Error('Client option query failed');
                    }
                    const dataList = Array.isArray(response.data.dataList) ? response.data.dataList : [];
                    this.clientOptions = dataList.filter(client => client != null
                        && (client.companyUUID == null || client.companyUUID === this.companyUUID)
                        && Number(client.isDelete) !== 1 && Number(client.syncStatus) !== 1);
                }).catch(() => {
                    if (requestSequence === this.clientOptionsRequestSequence) {
                        this.clientOptions = [];
                        this.clientOptionsLoadError = this.$t('invoice.clientOptionsLoadFailed');
                    }
                }).finally(() => {
                    if (requestSequence === this.clientOptionsRequestSequence) {
                        this.clientOptionsLoading = false;
                    }
                });
            },
            loadInitialClientOptions() {
                if (this.clientUUID === '') {
                    this.searchClientOptions('');
                    return;
                }
                const requestSequence = ++this.clientOptionsRequestSequence;
                this.clientOptionsLoading = true;
                this.clientOptionsLoadError = '';
                this.$axios.post('query_user_client', {
                    params: {
                        innerUUID: this.clientUUID,
                        companyUUID: this.companyUUID
                    }
                }).then(response => {
                    if (requestSequence !== this.clientOptionsRequestSequence) {
                        return;
                    }
                    if (response.data.errorCode !== 0) {
                        throw new Error('Selected client query failed');
                    }
                    const client = response.data.objectData;
                    this.clientOptions = client != null
                        && (client.innerUUID == null || client.innerUUID === this.clientUUID)
                        && (client.companyUUID == null || client.companyUUID === this.companyUUID)
                        && Number(client.isDelete) !== 1 && Number(client.syncStatus) !== 1
                        ? [client] : [];
                }).catch(() => {
                    if (requestSequence === this.clientOptionsRequestSequence) {
                        this.clientOptions = [];
                        this.clientOptionsLoadError = this.$t('invoice.clientOptionsLoadFailed');
                    }
                }).finally(() => {
                    if (requestSequence === this.clientOptionsRequestSequence) {
                        this.clientOptionsLoading = false;
                    }
                });
            },
            getTaxYearStart() {
                let taxYearStart = this.userSetting == null || this.userSetting.taxYearStart == null
                    || this.userSetting.taxYearStart === "" ? 0 : Number(this.userSetting.taxYearStart);
                let taxYearStartDate = new Date(taxYearStart * 1000);
                return {
                    month: taxYearStartDate.getMonth() + 1,
                    day: taxYearStartDate.getDate()
                };
            },
            getCurrentFiscalYearEnd() {
                let taxYearStart = this.getTaxYearStart();
                let currentDate = new Date();
                if (taxYearStart.month === 1 && taxYearStart.day === 1) {
                    return currentDate.getFullYear();
                }
                let currentYearStartDate = new Date(currentDate.getFullYear(), taxYearStart.month - 1, taxYearStart.day);
                if (currentDate.getTime() >= currentYearStartDate.getTime()) {
                    return currentDate.getFullYear() + 1;
                }
                return currentDate.getFullYear();
            },
            applyRouteFilters() {
                if (this.$route.query.invoiceNumberQuery !== undefined) {
                    this.invoiceNumberQuery = this.$route.query.invoiceNumberQuery;
                }
                if (this.$route.query.timePeriodType === "ALL_TIME") {
                    this.selectedYear = null;
                } else if (this.$route.query.year !== undefined) {
                    this.selectedYear = parseInt(this.$route.query.year);
                }
                let routeStartInvoiceDate = this.$route.query.startInvoiceDate;
                let routeEndInvoiceDate = this.$route.query.endInvoiceDate;
                if (routeStartInvoiceDate !== undefined || routeEndInvoiceDate !== undefined) {
                    const hasValidTimestamps = /^\d{10}$/.test(String(routeStartInvoiceDate))
                        && /^\d{10}$/.test(String(routeEndInvoiceDate));
                    if (hasValidTimestamps) {
                        let startDate = new Date(parseInt(routeStartInvoiceDate, 10) * 1000);
                        let endExclusiveDate = new Date(parseInt(routeEndInvoiceDate, 10) * 1000);
                        if (Number.isFinite(startDate.getTime()) && Number.isFinite(endExclusiveDate.getTime())
                            && startDate.getTime() < endExclusiveDate.getTime()) {
                            let endDate = new Date(endExclusiveDate.getFullYear(), endExclusiveDate.getMonth(),
                                endExclusiveDate.getDate() - 1);
                            this.invoiceDateRange = [startDate, endDate];
                        }
                    }
                }
                if (this.$route.query.paidStatus !== undefined) {
                    this.paidStatus = parseInt(this.$route.query.paidStatus);
                    if (this.paidStatus === 2) {
                        this.activeSummaryFilter = "paid";
                        this.invoiceStatusFilter = "paid";
                    } else {
                        this.activeSummaryFilter = "unpaid";
                        this.invoiceStatusFilter = "unpaid";
                    }
                }
                if (this.$route.query.emailed !== undefined) {
                    this.emailed = parseInt(this.$route.query.emailed);
                    this.activeSummaryFilter = "unsent";
                    this.invoiceStatusFilter = this.emailed === 0 ? "unsent" : "sent";
                }
                if (this.$route.query.overdue !== undefined) {
                    this.overdue = parseInt(this.$route.query.overdue);
                    this.activeSummaryFilter = "overdue";
                    this.invoiceStatusFilter = "overdue";
                }
                if (this.$route.query.innerUUID !== undefined) {
                    this.invoiceInnerUUID = this.$route.query.innerUUID;
                }
                if (this.$route.query.clientUUID !== undefined) {
                    this.clientUUID = this.$route.query.clientUUID;
                }
            },
            sortTable(column) {
                this.clearSelection();
                if (!column.order) {
                    this.orderName = "";
                    this.orderTpe = "";
                    localStorage.setItem("invoiceListOrderName", "");
                    localStorage.setItem("invoiceListOrderType", "");
                    this.queryList();
                    return;
                }
                this.orderName = column.prop;
                if (column.order === "ascending") {
                    this.orderTpe = 1
                } else if (column.order === "descending") {
                    this.orderTpe = 2
                }
                localStorage.setItem("invoiceListOrderName", this.orderName);
                localStorage.setItem("invoiceListOrderType", String(this.orderTpe));
                this.queryList();
            },
            changePage(val) {
                this.currentPage = val;
                this.saveListPagination();
                this.queryList();
            },
            changeTotalCount(val) {
                this.currentPage = 1;
                this.pageSize = val;
                this.saveListPagination();
                this.queryList();
            },
            handleRowHandle(row, column, event) {
                //点击每一条列表数据触发的方法
                if (column && (column.type === "selection" || column.className === "list-action-column")) {
                    return false;
                }
                openListDetail(this.$router, {path: 'invoiceUpdate', query: {innerUUID: row.innerUUID}}, event);
            },
            handleInvoiceAction(command, row) {
                if (command === 'markAsSent') {
                    this.markInvoiceAsSent(row);
                    return;
                }
                if (command === 'copy') {
                    this.openCopyDialog(row);
                    return;
                }
                if (command === 'delete') {
                    this.confirmDeleteInvoice(row);
                    return;
                }
            },
            markInvoiceAsSent(row) {
                return this.$axios.post('web_batch_action', {
                    params: {
                        entityType: 'DOCUMENT',
                        action: 'MARK_SENT',
                        documentType: 0,
                        companyUUID: this.companyUUID,
                        innerUUIDList: [row.innerUUID]
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        row.emailed = 1;
                        this.queryInvoiceDashboardSummary();
                        this.$message.success(this.$t('common.updateSuccess'));
                    }
                });
            },
            openRecordPaymentDialog(row) {
                let balanceDueMoney;
                try {
                    balanceDueMoney = limitServerNumber(row.balanceDueMoney);
                } catch (error) {
                    this.$message.error(this.$t('common.loadFailed'));
                    return;
                }
                if (Number(balanceDueMoney) <= 0.0001) {
                    this.$message.error(this.$t('invoice.errorNoOutstandingBalance'));
                    return;
                }
                this.recordPaymentInvoice = row;
                this.recordPaymentForm = {
                    paidMoney: balanceDueMoney,
                    paidDate: new Date(),
                    isFullPay: 1,
                    paidType: "",
                    note: ""
                };
                this.recordPaymentDialog = true;
            },
            handleRecordPaymentFullPayChange(value) {
                if (value === 1 && this.recordPaymentInvoice != null) {
                    this.recordPaymentForm.paidMoney =
                        limitServerNumber(this.recordPaymentInvoice.balanceDueMoney);
                }
            },
            handleRecordPaymentAmountInput(value) {
                if (this.recordPaymentInvoice == null) {
                    return;
                }
                try {
                    const paidMoney = this.normalizeRecordPaymentAmount(value);
                    const balanceDueMoney =
                        limitServerNumber(this.recordPaymentInvoice.balanceDueMoney);
                    this.recordPaymentForm.isFullPay = paidMoney === balanceDueMoney ? 1 : 0;
                } catch (error) {
                    this.recordPaymentForm.isFullPay = 0;
                }
            },
            normalizeRecordPaymentAmount(value) {
                const text = String(value).trim();
                if (!/^\d+(?:\.\d{1,5})?$/.test(text)) {
                    throw new Error('Payment amount is invalid');
                }
                return limitServerNumber(text);
            },
            saveRecordPayment() {
                if (this.recordPaymentInvoice == null) {
                    return;
                }
                const isFullPay = this.recordPaymentForm.isFullPay;
                let paidMoney = null;
                if (isFullPay !== 1) {
                    paidMoney = parseFloat(this.recordPaymentForm.paidMoney);
                    if (isNaN(paidMoney) || paidMoney <= 0) {
                        this.$message.error(this.$t('invoice.errorAmountMustBeGreaterThanZero'));
                        return;
                    }
                }
                const request = {
                    companyUUID: this.companyUUID,
                    invoiceUUID: this.recordPaymentInvoice.innerUUID,
                    paidMoney: paidMoney,
                    paidDate: toTmpStartOfDayTime(this.recordPaymentForm.paidDate),
                    paidType: this.recordPaymentForm.paidType,
                    note: this.recordPaymentForm.note,
                    isFullPay: isFullPay
                };
                this.recordPaymentLoading = true;
                this.$axios.post('web_record_payment', {
                    params: request
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.recordPaymentDialog = false;
                        this.recordPaymentInvoice = null;
                        this.$message.success(this.$t('common.saveSuccess'));
                        this.queryInvoiceDashboardSummary();
                        this.queryList();
                    }
                }).finally(() => {
                    this.recordPaymentLoading = false;
                });
            },
            //时间格式化
            dateFormat: function (row, column) {
                var timestamp = row[column.property] * 1000;
                if (timestamp == undefined) {
                    return "";
                }
                return formatEnglishDate(timestamp);
            },
            //格式化过期时间
            formatDueDate: function (row, column) {
                let timestamp = row[column.property] * 1000;
                if (timestamp == undefined) {
                    return "";
                }
                let dueDays = getDaysByDateString(Date.parse(new Date()), timestamp);
                if (dueDays === 0) {
                    return this.$t('invoice.dueDateToday');
                } else if (dueDays < 0) {
                    return this.$t('invoice.dueDateDaysAgo', {days: Math.abs(dueDays)});
                } else {
                    return this.$t('invoice.dueDateInDays', {days: dueDays});
                }
            },
            //格式化过期时间
            formatDueDate2: function (row, scope) {

                if (scope.row.totalMoney <= 0) {
                    return "";
                }
                if (scope.row.paidStatus === 2) {
                    return "";
                }
                let timestamp = row * 1000;
                if (timestamp == undefined) {
                    return "";
                }
                let dueDays = getDaysByDateString(Date.parse(new Date()), timestamp);
                if (dueDays === 0) {
                    return this.$t('invoice.dueDateToday');
                } else if (dueDays < 0) {
                    return this.$t('invoice.dueDateDaysAgo', {days: Math.abs(dueDays)});
                } else {
                    return this.$t('invoice.dueDateInDays', {days: dueDays});
                }
            },

            //金钱格式化,增加金钱符号和分隔符
            amountFormat: function (row, column) {
                var columnAmount = row[column.property];
                if (columnAmount == undefined) {
                    return "";
                }
                return xiaoshudianfenge(columnAmount, this.userSetting);
            },
            formatSummaryMoney(value) {
                return xiaoshudianfenge(value, this.userSetting);
            },
            openCopyDialog(row) {
                this.copySourceInvoice = row;
                this.copyForm = {
                    targetType: 0,
                    copyClient: true,
                    copyItems: true,
                    copyAttachments: true
                };
                this.copyDialog = true;
            },
            performCopy() {
                if (this.copySourceInvoice == null) {
                    return;
                }
                this.copyLoading = true;
                return this.$axios.post('web_query_invoice', {
                    params: {
                        innerUUID: this.copySourceInvoice.innerUUID,
                        companyUUID: this.companyUUID
                    }
                }).then(response => {
                    validateDocumentDetailResponse(response.data, this.companyUUID,
                        this.copySourceInvoice.innerUUID, [0]);
                    const sourceData = {
                        detail: response.data,
                        options: {
                            targetType: this.copyForm.targetType,
                            copyClient: this.copyForm.copyClient,
                            copyItems: this.copyForm.copyItems,
                            copyAttachments: this.copyForm.copyAttachments,
                            isConvert: false
                        }
                    };
                    localStorage.setItem('copySourceData', JSON.stringify(sourceData));
                    this.copyDialog = false;
                    if (this.copyForm.targetType === 0) {
                        this.$router.push('/invoiceCreate');
                    } else if (this.copyForm.targetType === 1) {
                        this.$router.push('/createEstimate?type=1');
                    } else {
                        this.$router.push('/createEstimate?type=2');
                    }
                }).catch(() => {
                    this.$message.error(this.$t('invoice.copyFailed'));
                }).finally(() => {
                    this.copyLoading = false;
                });
            },
            confirmDeleteInvoice(row) {
                this.$confirm(this.$t('invoice.confirmDelete'), this.$t('common.warning'), {
                    confirmButtonText: this.$t('common.ok'),
                    cancelButtonText: this.$t('common.cancel'),
                    type: 'warning'
                }).then(() => {
                    this.performDeleteInvoice(row);
                }).catch(() => {});
            },
            performDeleteInvoice(row) {
                this.deleteLoading = true;
                return this.$axios.post('web_batch_action', {
                    params: {
                        entityType: 'DOCUMENT',
                        action: 'DELETE',
                        documentType: 0,
                        companyUUID: this.companyUUID,
                        innerUUIDList: [row.innerUUID]
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.$message.success(this.$t('invoice.deleteSuccess'));
                        this.moveToExistingInvoicePage(1);
                        this.clearSelection();
                        this.queryInvoiceDashboardSummary();
                        this.queryList();
                    }
                }).finally(() => {
                    this.deleteLoading = false;
                });
            }

        },
        created() {

        },
        data() {
            let storedOrderName = localStorage.getItem("invoiceListOrderName");
            let storedOrderType = localStorage.getItem("invoiceListOrderType");
            if (storedOrderName === null || storedOrderType === null) {
                storedOrderName = "invoiceStartDate";
                storedOrderType = "2";
            }
            return {
                tableData: [],
                loading: true,
                listLoadError: "",
                summaryLoadError: "",
                listRequestSequence: 0,
                summaryRequestSequence: 0,
                clientOptionsRequestSequence: 0,
                currentPage: 1,
                totalCount: 0,
                pageSize: 50,
                invoiceNumberQuery: "",
                invoiceStatusFilter: "",
                invoiceDateRange: null,
                clientOptions: [],
                clientOptionsLoading: false,
                clientOptionsLoadError: '',
                selectedRows: [],
                selectionMode: false,
                batchLoadingAction: '',
                exporting: false,
                selectedYear: null,
                activeSummaryFilter: "",
                paidStatus: null,
                emailed: null,
                overdue: null,
                invoiceInnerUUID: "",
                clientUUID: "",
                orderName: storedOrderName,
                orderTpe: storedOrderType === "" ? "" : parseInt(storedOrderType, 10),
                companyUUID: '',
                companyName: '',
                companyContextLoaded: false,
                userSetting: {},
                cantAddInvoice: false,
                totalTypeCount: 0,
                invoiceSummary: {
                    overdueInvoiceCount: 0,
                    overdueInvoiceTotalMoney: 0,
                    paidInvoiceCount: 0,
                    paidInvoiceTotalMoney: 0,
                    unpaidInvoiceCount: 0,
                    unpaidInvoiceTotalMoney: 0,
                    unsentInvoiceCount: 0,
                    unsentInvoiceTotalMoney: 0
                },
                recordPaymentDialog: false,
                recordPaymentLoading: false,
                recordPaymentInvoice: null,
                recordPaymentForm: {
                    paidMoney: '0',
                    paidDate: new Date(),
                    isFullPay: 1,
                    paidType: "",
                    note: ""
                },
                copyDialog: false,
                copyLoading: false,
                deleteLoading: false,
                copySourceInvoice: null,
                copyForm: {
                    targetType: 0,
                    copyClient: true,
                    copyItems: true,
                    copyAttachments: true
                },
                stickyScrollbarVisible: false,
                stickyScrollbarLeft: 0,
                stickyScrollbarViewportWidth: 0,
                stickyScrollbarContentWidth: 0,
                stickyScrollbarThumbWidth: 0,
                stickyScrollbarThumbLeft: 0,
                stickyScrollbarScrollLeft: 0,
                stickyScrollbarDragStartX: 0,
                stickyScrollbarDragStartLeft: 0,
                invoiceTableScrollBody: null,
                searchWatcherPaused: true
            }
        },
        mounted() {
            this.applyRouteFilters();
            this.queryCompanyAndInvoiceList();
            window.addEventListener('resize', this.refreshInvoiceStickyScrollbar);
            const queryByInvoiceNumber = debounce(() => {
                this.clearSelection();
                this.currentPage = 1;
                this.saveListPagination();
                this.queryList();
            }, 500);
            this.$watch('invoiceNumberQuery', () => {
                if (this.searchWatcherPaused) {
                    return;
                }
                queryByInvoiceNumber();
            })
        },
        beforeRouteLeave(to, from, next) {
            this.saveListQueryState();
            next();
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.refreshInvoiceStickyScrollbar);
            this.stopInvoiceStickyScrollbarDrag();
            if (this.invoiceTableScrollBody != null) {
                this.invoiceTableScrollBody.removeEventListener(
                    'scroll', this.syncStickyScrollbarFromInvoiceTable
                );
            }
        }
    }
</script>
